import { InputText } from 'components/Ui-Kits/InputText'
import { Text } from 'components/Ui-Kits/Text'
import { useQuery } from 'hooks/query/useQuery'
import { useSetQuery } from 'hooks/query/useSetQuery'
import { useAppTranslation } from 'i18n/hooks'
import { any } from 'prop-types'
import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../../globalStyles'
import { Filter } from './Filter'
import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Categories } from './Categories'
import { createPortal } from 'react-dom'
import { intersection } from 'lodash'

export const Sidebar = ({ categoryOptions, tagOptions, materials, conditions, rulers, countries, products }) => {
  const { t } = useAppTranslation()
  const setQuery = useSetQuery()
  const query = useQuery()

  const [showFilters, setShowFilters] = useState(false)

  const hasProductsForFilter = useCallback(
    (name, value) => {
      const filterData = { ...query }

      filterData[name] = value

      const { materials, category, conditions, rulers, references, tags, countries } = filterData

      return products.filter(
        ({ technicalDetails, tags: pTags, categories: productCategories }) =>
          (!materials?.length || materials.includes(technicalDetails?.material)) &&
          (!category || category == technicalDetails?.auctionCategory?.id || productCategories?.includes(category)) &&
          (!tags?.length || !!intersection(tags, pTags?.map(({ id }) => id) || []).length) &&
          (!conditions?.length || conditions.includes(technicalDetails?.condition)) &&
          (!countries?.length || countries.includes(technicalDetails?.country)) &&
          (!rulers?.length || rulers.includes(technicalDetails?.ruler)) &&
          (!references?.length || references.includes(technicalDetails?.reference))
      ).length
    },
    [query, products]
  )

  return (
    <Wrap>
      <InputText
        defaultValue={query.q}
        onChange={e => setQuery({ q: e.target.value })}
        placeholder={t('search')}
        noBorder
      />
      <Categories title={t('category')} options={categoryOptions} name="category" />
      <FilterWrap open={showFilters}>
        <FilterTitle onClick={() => setShowFilters(showFilters => !showFilters)}>
          <Text size={1.25} color="primary">
            {t('filters')}
          </Text>
          <Icon icon={showFilters ? faAngleUp : faAngleDown} />
        </FilterTitle>
        <FilterContent show={showFilters}>
          <Filter title={t('tags')} options={tagOptions} name="tags" hasProductsForFilter={hasProductsForFilter} />
          <Filter
            title={t('countries')}
            options={countries}
            name="countries"
            hasProductsForFilter={hasProductsForFilter}
          />
          <Filter
            title={t('condition')}
            options={conditions}
            name="conditions"
            hasProductsForFilter={hasProductsForFilter}
          />
          <Filter
            title={t('material')}
            options={materials}
            name="materials"
            hasProductsForFilter={hasProductsForFilter}
          />
          <Filter title={t('ruler')} options={rulers} name="rulers" hasProductsForFilter={hasProductsForFilter} />
        </FilterContent>
      </FilterWrap>
      {showFilters && createPortal(<TransparentWrap onClick={() => setShowFilters(false)} />, document.body)}
    </Wrap>
  )
}

Sidebar.propTypes = {
  categoryOptions: any,
  tagOptions: any,
  materials: any,
  conditions: any,
  rulers: any,
  countries: any,
  products: any,
}

const Wrap = styled.div`
  width: 100%;
  padding-right: 0;
  margin-bottom: 2rem;
  top: 0;
  ${theme.breakpoints.tablet} {
    width: 250px;
    padding-right: 2rem;
    position: sticky;
    max-height: 100vh;
    overflow: auto;
  }
`

const FilterWrap = styled.div`
  background-color: var(--color-white);
  position: relative;
  padding: 1rem;
  z-index: ${({ open }) => (open ? 1000 : 'auto')};
  ${({ open }) => open && `margin: 0 -1rem;`}

  ${theme.breakpoints.tablet} {
    margin: 0;
    padding: 0;
    background-color: var(--color-transparent);
  }
`

const FilterContent = styled.div`
  display: ${({ show }) => (show ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  background-color: var(--color-white);
  z-index: ${({ show }) => (show ? 1000 : 'auto')};
  width: 100%;
  padding: 1rem;

  ${theme.breakpoints.tablet} {
    margin-top: 0;
    display: block;
    position: relative;
    top: auto;
    left: auto;
    background-color: none;
    padding: 0;
    background-color: var(--color-transparent);
  }
`

const FilterTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 2.5rem;

  ${theme.breakpoints.tablet} {
    display: none;
  }
`
const Icon = styled(FontAwesomeIcon)`
  color: var(--color-primary);
  font-size: 1rem;
`
const TransparentWrap = styled.div`
  z-index: 99;
  display: block;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  ${theme.breakpoints.tablet} {
    display: none;
  }
`
